//CRN_20260513 - https://riskline.readme.io/reference/apis-overview

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ConfigSystemParams, msSize } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RisklineSharedService } from 'src/app/core/services/riskline/riskline-shared.service';
import { RisklineTravelSearchService } from 'src/app/core/services/riskline/riskline-travel-search.service';
import { Subscription } from 'rxjs';
import { TravelSearchRequestDTO, TravelSearchRequestDTO_EmptyObject, TravelSearchResponseDTO } from 'src/app/core/models/system/riskline/riskline-travel-search.dto';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

@Component({
  selector   : 'app-riskline-travel-search-modal-form',
  templateUrl: './riskline-travel-search-modal-form.component.html',
  styleUrls  : ['./riskline-travel-search-modal-form.component.scss']
})
export class RisklineTravelSearchModalFormComponent implements OnDestroy, OnInit {

  @Input() public defaultDestination: string | undefined;
  @Input() public defaultFromDate   : string | undefined;
  @Input() public defaultOrigin     : string | undefined;
  @Input() public defaultToDate     : string | undefined;

  @Output() public formCancelled: EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('risklineTravelSearchModalForm', { static: false }) public risklineTravelSearchModalForm!: TemplateRef<void>;

  private modalRef : NgbModalRef | undefined;
  private searchSub: Subscription | undefined;

  public formSubmitted  : boolean=false;
  public isLoading      : boolean=false;
  public result         : TravelSearchResponseDTO | null=null;
  public searchForm!    : UntypedFormGroup;
  public validationError: string='';

  get modalBackGround() {
    let value: string | boolean=this.configSystemService.getValue(ConfigSystemParams.ModalBackGround, "TRUE");
    return value=value.toUpperCase()=="STATIC" ? "static" : (value.toUpperCase()=="TRUE" ? true : false);
  }

  constructor(
    private configSystemService        : ConfigSystemService,
    private exceptionsService          : ExceptionsService,
    private formBuilder                : UntypedFormBuilder,
    private modalService               : NgbModal,
    private risklineSharedService      : RisklineSharedService,
    private risklineTravelSearchService: RisklineTravelSearchService
  ) { }

  public ngOnInit(): void {
    try {
      this.buildForm();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.searchSub?.unsubscribe();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public get form() {
    return this.searchForm.controls;
  }

  public cancelForm(): void {
    try {
      this.formCancelled.emit();
      this.modalRef?.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public openForm(): void {
    try {
      this.formSubmitted  =false;
      this.result         =null;
      this.validationError='';

      this.buildForm();

      if (this.defaultDestination)
        this.searchForm.patchValue({ destination: this.defaultDestination });

      if (this.defaultFromDate)
        this.searchForm.patchValue({ fromDate: this.defaultFromDate });

      if (this.defaultOrigin)
        this.searchForm.patchValue({ origin: this.defaultOrigin });

      if (this.defaultToDate)
        this.searchForm.patchValue({ toDate: this.defaultToDate });

      this.openModal();
    }
    catch(e) {
      this.formCancelled.emit();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public submitForm(): void {
    try {
      this.formSubmitted  =true;
      this.validationError='';

      if (!this.searchForm.valid)
        return;

      const dateValidation=this.risklineSharedService.validateDateRange(this.searchForm.value.fromDate, this.searchForm.value.toDate);

      if (!dateValidation.isValid) {
        this.validationError=dateValidation.message;

        return;
      }

      const request: TravelSearchRequestDTO=TravelSearchRequestDTO_EmptyObject();
      request.dateOutbound=this.searchForm.value.fromDate;
      request.dateReturn  =this.searchForm.value.toDate;
      request.destination =this.searchForm.value.destination;
      request.nationality =this.searchForm.value.nationality || undefined;
      request.origin      =this.searchForm.value.origin;

      this.isLoading=true;
      this.searchSub?.unsubscribe();
      this.searchSub=this.risklineTravelSearchService.search(request).subscribe({
        next : data => {
          this.result   =data;
          this.isLoading=false;
        },
        error: err => {
          this.isLoading=false;
          this.exceptionsService.handleException(new Error(err));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public riskBadgeStyle(riskLevelId: string | undefined): { [key: string]: string; } {
    try {
      return this.risklineSharedService.getRiskLevelBadgeStyle(riskLevelId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { 'background-color': '#878a9926', 'color': '#878a99' };
    }
  }

  public trustHtml(value: string | undefined): any {
    try {
      return this.risklineSharedService.trustHtml(value);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  private buildForm(): void {
    try {
      this.searchForm=this.formBuilder.group({
        destination: ['', [Validators.required]],
        fromDate   : ['', [Validators.required]],
        nationality: [''],
        origin     : ['', [Validators.required]],
        toDate     : ['', [Validators.required]]
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private openModal(): void {
    try {
      this.modalRef=this.modalService.open(this.risklineTravelSearchModalForm, {
        backdrop: this.modalBackGround,
        centered: true,
        keyboard: false,
        size    : msSize.Large
      });
    }
    catch(e) {
      this.formCancelled.emit();
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
